import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import { Box, Collapse, CssBaseline, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { menuItems } from '../constants';
import { authService } from '../services';
import theme from '../theme';

const drawerWidth = 280;

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  leftButton?: React.ReactNode;
  showBackButton?: boolean;
  onBackClick?: () => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title, leftButton, showBackButton = false, onBackClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const currentUser = authService.getCurrentUser();

  const handleLogout = async () => {
    try {
      await authService.revokeToken();
      authService.logout();
    } catch (err: any) {
      console.error('Logout error:', err);
    }
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // For sub items, use exact match instead of startsWith
    if (path.includes('/games/')) {
      return location.pathname === path;
    }
    // For main items, use startsWith
    return location.pathname.startsWith(path);
  };

  const handleExpandClick = (itemText: string) => {
    setExpandedItems((prev) => (prev.includes(itemText) ? prev.filter((item) => item !== itemText) : [...prev, itemText]));
  };

  // Auto-expand parent when sub item is active
  React.useEffect(() => {
    const newExpandedItems: string[] = [];
    menuItems.forEach((item) => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some((subItem) => isActive(subItem.path));
        if (hasActiveSubItem && !expandedItems.includes(item.text)) {
          newExpandedItems.push(item.text);
        }
      }
    });
    if (newExpandedItems.length > 0) {
      setExpandedItems((prev) => [...prev, ...newExpandedItems]);
    }
  }, [location.pathname]);

  const renderMenuItem = (item: any, isSubItem = false) => {
    const active = isActive(item.path);
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedItems.includes(item.text);

    return (
      <React.Fragment key={item.text}>
        <ListItem
          disablePadding
          sx={{
            mb: 1,
            px: 0,
            position: 'relative',
            zIndex: 1,
            marginLeft: isSubItem ? '10px' : '0px',
          }}
        >
          <ListItemButton
            component={hasSubItems ? 'div' : Link}
            to={hasSubItems ? undefined : item.path}
            onClick={hasSubItems ? () => handleExpandClick(item.text) : undefined}
            sx={{
              backgroundColor: active ? '#fff' : 'transparent',
              color: active ? '#FF1B1B' : '#fff',
              borderLeft: active ? '4px solid #FF1B1B' : 'none',
              borderTopLeftRadius: active ? '30px' : 0,
              borderBottomLeftRadius: active ? '30px' : 0,
              position: 'relative',
              overflow: 'visible',
              marginLeft: '16px',
              '&::before': active
                ? {
                  content: '""',
                  position: 'absolute',
                  right: '0px',
                  top: '-24px',
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'transparent',
                  borderBottomRightRadius: '24px',
                  zIndex: 1,
                  boxShadow: '5px 6px 0px 5px var(--color-white)',
                }
                : {},
              '&::after': active
                ? {
                  content: '""',
                  position: 'absolute',
                  right: '0px',
                  bottom: '-24px',
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'transparent',
                  borderTopRightRadius: '24px',
                  zIndex: 1,
                  boxShadow: '6px -3px 0px 3px var(--color-white)',
                }
                : {},
              '&:hover': {
                backgroundColor: active ? '#fff' : 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiListItemIcon-root': {
                color: active ? '#FF1B1B' : '#fff',
                zIndex: 2,
              },
              '& .MuiListItemText-primary': {
                color: active ? '#FF1B1B' : '#fff',
                fontWeight: active ? 600 : 400,
                zIndex: 2,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: active ? '#FF1B1B' : 'white',
                minWidth: 40,
                '& img': {
                  filter: active ? 'invert(27%) sepia(98%) saturate(7495%) hue-rotate(357deg) brightness(97%) contrast(114%)' : 'brightness(0) invert(1)',
                },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '16px',
                fontWeight: active ? 600 : 400,
                letterSpacing: '0.15px',
                color: active ? '#FF1B1B' : 'white',
              }}
            />
            {hasSubItems && (
              <Box
                sx={{
                  color: active ? '#FF1B1B' : 'white',
                  transition: 'transform 0.2s ease-in-out',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <ExpandMore />
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {hasSubItems && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subItems.map((subItem: any) => renderMenuItem(subItem, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        color: 'white',
        py: 3,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box component="img" src={require('../assets/images/logo.png')} alt="Logo CMS" sx={{ width: 140, height: 'auto', mb: 1, margin: 'auto' }} />
      </Box>

      <List sx={{ px: 0 }}>{menuItems.map((item) => {
        const isAllowed = authService.isAllowedPermission(item.requireRole);

        if (item.requireRole && !isAllowed) {
          return null;
        }
        return renderMenuItem(item);
      })}</List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default }}>
      <CssBaseline />
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              background: theme.palette.primary.main,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            px: '30px',
            backgroundColor: theme.palette.background.default,
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {showBackButton && (
              <IconButton onClick={handleBackClick} sx={{ mr: 1 }}>
                <ArrowBackIcon sx={{ color: '#666' }} />
              </IconButton>
            )}
            {title && (
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {title}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: theme.palette.background.default,
              borderRadius: '25px',
              px: 2,
              py: 1,
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#E0E0E0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box component="img" src={require('../assets/icons/ic_user.svg').default} alt="User" sx={{ width: 16, height: 16 }} />
            </Box>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
              {currentUser?.username ?? 'Admin1'}
            </Typography>
            <Box
              component="button"
              onClick={handleLogout}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#E0E0E0',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#D0D0D0',
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: '16px', color: '#666' }} />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
